import React, { useMemo } from 'react';
import { Typography, Link, Divider, Stack, Paper } from '@mui/material';

import { FooterLink, ProductFooterProps } from './types';
import { getCopyrightText, getLegalNotices } from './defaultContent';

const ProductFooter = ({
  productCode,
  links,
  copyright = [],
  sx = [],
  variant = 'outlined',
  square = true,
  ...rest
}: ProductFooterProps) => {
  return (
    <Paper
      sx={[
        {
          color: 'inherit',
          bgcolor: 'inherit',
          marginTop: 'calc(10%)',
          width: '100%',
          py: 0.5,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      component='footer'
      square={square}
      variant={variant}
      {...rest}
    >
      <Stack direction={{ sm: 'row', xs: 'column' }} justifyContent='space-around' alignItems='center'>
        <CopyrightNotice copyright={copyright} productCode={productCode} />
        <LegalNotices productCode={productCode} links={links} />
      </Stack>
    </Paper>
  );
};

const CopyrightNotice = ({ productCode, copyright = [] }: Pick<ProductFooterProps, 'productCode' | 'copyright'>) => {
  const copyrightText = useMemo(
    () => (copyright.length ? copyright : getCopyrightText(productCode)),
    [productCode, copyright]
  );

  return (
    <Stack direction='column'>
      {copyrightText.map((line, index) => (
        <Typography variant='caption' component='div' key={index}>
          {line}
        </Typography>
      ))}
    </Stack>
  );
};

const LegalNotices = ({ productCode, links = [] }: Pick<ProductFooterProps, 'links' | 'productCode'>) => {
  const content = useMemo(() => [...links, getLegalNotices(productCode)], [productCode, links]);

  return (
    <Stack direction='row' divider={<Divider orientation='vertical' variant='middle' flexItem />} spacing={1}>
      {content.map((link, index) => (
        <LegalNotice key={index} link={link} />
      ))}
    </Stack>
  );
};

const LegalNotice = ({ link: { text, href, target = '_self' } }: { link: FooterLink }) => {
  return (
    <Link href={href} target={target} sx={{ color: 'inherit', textDecorationColor: 'inherit' }}>
      <Typography variant='caption'>{text}</Typography>
    </Link>
  );
};

export { ProductFooter };
